import { useMemo, useState, useCallback, useContext } from 'react'
import { Drawer } from 'antd'
import { ReactFlowProvider } from '@xyflow/react'
import { VerticalContext } from 'store/verticals/Context'
import { Graph } from 'uikit/graphs/default'
import type { NodeTypes, EdgeTypes } from 'uikit/graphs/default'
import { BaseEdge } from 'uikit/graphs/default'
import { useGraphModel } from '../core/hooks/use-graph-model'
import { useGraphView } from '../core/hooks/use-graph-view'
import { SourceNode } from '../core/nodes/source-node'
import { ContactInfoNode } from '../core/nodes/contact-info-node'
import { IdentityNode } from '../core/nodes/identity-node'
import { GraphHeader } from '../core/controls/graph-header'
import { DetailsPanel } from '../core/controls/details-panel/details-panel'
import { NodeProperties } from '../core/controls/details-panel/node-properties'
import { IdentityDetails } from '../core/controls/details-panel/identity-details'
import { EmptyPropertiesState } from '../core/controls/details-panel/empty-properties-state'
import { EmptyGraphState } from '../core/controls/empty-graph-state'
import { GraphLoadingOverlay } from '../core/controls/graph-loading-overlay'
import { normalizeApiData } from '../core/utils/transform-api-data'
import { EdgePropertiesReadonly } from './edge-properties-readonly'
import { useEvidenceLayers, type EvidenceLayers } from './evidence-layer-menu'
import { GraphDialogHeader } from './graph-dialog-header'
import {
  BusinessEntityTypes,
  type ApiGraphData,
  type RawApiGraphData,
  type NetworkNode,
  type NetworkEdge,
  type NetworkNodeData,
  type NetworkEdgeData,
} from '../core/types'
import styles from './view-case-graph.module.scss'
import { GraphHelpButton } from '../core/controls/graph-help-button'
import { viewCaseHelpSections } from './help-sections'

const nodeTypes: NodeTypes = {
  sourceNode: SourceNode,
  contactInfoNode: ContactInfoNode,
  identityNode: IdentityNode,
}

const edgeTypes: EdgeTypes = {
  baseEdge: BaseEdge as any,
}

export interface ViewCaseGraphProps {
  entityName: string
  entityId: string
  graphData: RawApiGraphData | null
  isLoading?: boolean
  onClose: () => void
}

function ViewCaseGraphInner({ entityName, entityId, graphData, isLoading, onClose }: ViewCaseGraphProps) {
  const { evidenceLayers, handleEvidenceToggle } = useEvidenceLayers()
  const { verticalEntity } = useContext(VerticalContext)
  const [identityDrawerOpen, setIdentityDrawerOpen] = useState(false)

  const openIdentityDrawer = useCallback(() => setIdentityDrawerOpen(true), [])
  const closeIdentityDrawer = useCallback(() => setIdentityDrawerOpen(false), [])

  const normalizedData: ApiGraphData | undefined = useMemo(() => {
    if (!graphData || !graphData.nodes?.length) return undefined
    return normalizeApiData(graphData)
  }, [graphData])

  const { nodes, edges, onNodesChange, onEdgesChange } = useGraphModel({
    initialData: normalizedData,
  })

  const availableLayers = useMemo<EvidenceLayers>(() => {
    const types = new Set(nodes.map((n: NetworkNode) => (n.data as NetworkNodeData).nodeType))
    return {
      phone: types.has(BusinessEntityTypes.PHONE),
      email: types.has(BusinessEntityTypes.EMAIL),
      location: types.has(BusinessEntityTypes.LOCATION),
    }
  }, [nodes])

  const filteredNodes = useMemo(() => {
    return nodes.filter((node: NetworkNode) => {
      const data = node.data as NetworkNodeData
      const nodeType = data.nodeType
      if (nodeType === BusinessEntityTypes.PHONE && !evidenceLayers.phone) return false
      if (nodeType === BusinessEntityTypes.EMAIL && !evidenceLayers.email) return false
      if (nodeType === BusinessEntityTypes.LOCATION && !evidenceLayers.location) return false
      return true
    })
  }, [nodes, evidenceLayers])

  const filteredNodeIds = useMemo(() => new Set(filteredNodes.map(n => n.id)), [filteredNodes])

  const filteredEdges = useMemo(() => {
    return edges.filter((edge: NetworkEdge) => filteredNodeIds.has(edge.source) && filteredNodeIds.has(edge.target))
  }, [edges, filteredNodeIds])

  const view = useGraphView({ nodes: filteredNodes, edges: filteredEdges })

  const hasGraphData = normalizedData && normalizedData.nodes.length > 0
  const isEmpty = !isLoading && !hasGraphData

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog}>
        <GraphDialogHeader entityName={entityName} onClose={onClose} />

        <div className={styles.graphBody}>
          <div className={styles.graphArea}>
            {isEmpty ? (
              <EmptyGraphState message='No graph data available for this lead' onCancel={onClose} />
            ) : (
              <>
                <GraphHeader
                  nodes={view.nodeOptions}
                  onNodeSelect={view.handleNodeSelect}
                  evidenceLayers={evidenceLayers}
                  availableLayers={availableLayers}
                  onEvidenceToggle={handleEvidenceToggle}
                />
                <GraphHelpButton title='Help Center' sections={viewCaseHelpSections} />
                <Graph
                  nodes={filteredNodes}
                  edges={view.edgesWithSelection}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={view.handleNodeClick}
                  onEdgeClick={view.handleEdgeClick}
                  onPaneClick={view.handlePaneClick}
                  onSelectionChange={view.onSelectionChange}
                  nodesConnectable={false}
                  nodesDraggable={true}
                  elementsSelectable={true}
                  deleteKeyCode={null}
                />
                <GraphLoadingOverlay isVisible={isLoading} />
              </>
            )}
          </div>

          {!isEmpty && (
            <DetailsPanel
              title={view.selectedEdge ? 'Edge Properties' : 'Node Properties'}
              isOpen={view.isDrawerOpen}
              onToggle={view.handleDrawerToggle}
              onClose={onClose}
            >
              {view.selectedNode ? (
                <NodeProperties
                  data={view.selectedNode.data as NetworkNodeData}
                  entityId={entityId}
                  onViewIdentity={openIdentityDrawer}
                />
              ) : view.selectedEdge ? (
                <EdgePropertiesReadonly data={view.selectedEdge.data as NetworkEdgeData} />
              ) : (
                <EmptyPropertiesState />
              )}
            </DetailsPanel>
          )}

          <Drawer
            title='Identity Details'
            placement='right'
            width={360}
            open={identityDrawerOpen}
            onClose={closeIdentityDrawer}
            getContainer={false}
            maskClosable
            styles={{ mask: { background: 'transparent' }, body: { padding: 0 } }}
          >
            {verticalEntity && <IdentityDetails entityDetails={verticalEntity.entityDetails} />}
          </Drawer>
        </div>
      </div>
    </div>
  )
}

export function ViewCaseGraph(props: ViewCaseGraphProps) {
  return (
    <ReactFlowProvider>
      <ViewCaseGraphInner {...props} />
    </ReactFlowProvider>
  )
}
